import React, { useState, useEffect, useRef } from 'react'
import { useFloatMessage, MESSAGE_TYPE } from '../../Providers/FloatMessage'
import { SecondaryBase } from '../Commons/SecondaryBase'
import { ResetCurrentDynamic as BLResetCurrentDynamic } from './../../../utilities/BL/Zones'

// Importamos los servicios del BL (Asegúrate de ajustar la ruta)
import { 
    ShowInteractiveMessage, 
    HideInteractiveMessage, 
    SetInteractiveChatConfig, 
    GetQuickReplies, 
    SaveQuickReply, 
    DeleteQuickReply 
} from './../../../utilities/BL/InteractiveChat'




/**
 * ==============================================================================
 * COMPONENTE: INTERACTIVE CHAT DASHBOARD (ADMIN)
 * Panel de control optimizado para celulares para enviar mensajes a la pantalla.
 * ==============================================================================
 */
const InteractiveChat = ({ getOutOfTheDynamic }) => {
    const { showMessage } = useFloatMessage();

    // --------------------------------------------------------------------------------
    // 1. ESTADOS DE DATOS Y MENSAJE
    // --------------------------------------------------------------------------------
    const [currentMessage, setCurrentMessage] = useState("");
    const [isMessageVisible, setIsMessageVisible] = useState(false);
    const [quickReplies, setQuickReplies] = useState([]);
    const [isLoadingReplies, setIsLoadingReplies] = useState(false);

    // --------------------------------------------------------------------------------
    // 2. ESTADOS DE CONFIGURACIÓN VISUAL
    // --------------------------------------------------------------------------------
    const [config, setConfig] = useState({
        textColor: '#FFFFFF',
        strokeColor: '#000000',
        hasBackground: true,
        backgroundColor: '#000000', // Hex para fácil uso con input color
        scale: 1.0,
        positionY: 100,
        rotation: 0,
        fontFamily: 'Montserrat'
    });

    const isFirstRender = useRef(true);

    // --------------------------------------------------------------------------------
    // 3. CICLO DE VIDA (Carga inicial y Sincronización de Configuración)
    // --------------------------------------------------------------------------------
    
    // Cargar Respuestas Rápidas al montar
    useEffect(() => {
        const loadReplies = async () => {
            setIsLoadingReplies(true);
            const response = await GetQuickReplies();
            if (response.success) {
                setQuickReplies(response.data || []);
            }
            setIsLoadingReplies(false);
        };
        loadReplies();
    }, []);

    // Efecto Debounce para sincronizar los cambios de apariencia en vivo
    useEffect(() => {
        if (isFirstRender.current) {
            isFirstRender.current = false;
            return; // No sincronizamos en el primer render para no sobreescribir sin querer
        }

        const debounceTimer = setTimeout(async () => {
            // Mandamos el color de fondo pero le inyectamos opacidad usando "rgba" temporalmente para el OBS
            // Convertimos hex a rgba (ej. #000000 -> rgba(0,0,0,0.7))
            const hexToRgb = (hex) => {
                const r = parseInt(hex.slice(1, 3), 16);
                const g = parseInt(hex.slice(3, 5), 16);
                const b = parseInt(hex.slice(5, 7), 16);
                return `${r}, ${g}, ${b}`;
            };

            const payloadConfig = {
                ...config,
                backgroundColor: `rgba(${hexToRgb(config.backgroundColor)}, 0.7)`
            };

            await SetInteractiveChatConfig(payloadConfig);
        }, 300); // 300ms de debounce

        return () => clearTimeout(debounceTimer);
    }, [config]);

    // --------------------------------------------------------------------------------
    // 4. HANDLERS DE MENSAJES (PROYECTAR / OCULTAR)
    // --------------------------------------------------------------------------------
    const handleShowMessage = async () => {
        if (!currentMessage.trim()) {
            showMessage({ type: MESSAGE_TYPE.WARNING, text: "Escribe un mensaje primero." });
            return;
        }

        try {
            const res = await ShowInteractiveMessage(currentMessage);
            if (res.success) {
                setIsMessageVisible(true);
                showMessage({ type: MESSAGE_TYPE.SUCCESS, text: "Mensaje proyectado en pantalla." });
            } else {
                showMessage({ type: MESSAGE_TYPE.BAD, text: res.message });
            }
        } catch (error) {
            showMessage({ type: MESSAGE_TYPE.BAD, text: "Error al comunicar con la pantalla." });
        }
    };

    const handleHideMessage = async () => {
        try {
            const res = await HideInteractiveMessage();
            if (res.success) {
                setIsMessageVisible(false);
                showMessage({ type: MESSAGE_TYPE.SUCCESS, text: "Mensaje ocultado." });
            }
        } catch (error) {
            showMessage({ type: MESSAGE_TYPE.BAD, text: "Error al quitar el mensaje." });
        }
    };

    // --------------------------------------------------------------------------------
    // 5. HANDLERS DE RESPUESTAS RÁPIDAS
    // --------------------------------------------------------------------------------
    const handleSaveQuickReply = async () => {
        if (!currentMessage.trim()) return;
        
        try {
            const res = await SaveQuickReply(currentMessage);
            if (res.success) {
                setQuickReplies(prev => [res.data, ...prev]);
                showMessage({ type: MESSAGE_TYPE.SUCCESS, text: "Guardado en respuestas rápidas." });
            }
        } catch (error) {
            showMessage({ type: MESSAGE_TYPE.BAD, text: "No se pudo guardar." });
        }
    };

    const handleDeleteQuickReply = async (id) => {
        try {
            const res = await DeleteQuickReply(id);
            if (res.success) {
                setQuickReplies(prev => prev.filter(r => r.id !== id));
                showMessage({ type: MESSAGE_TYPE.SUCCESS, text: "Respuesta eliminada." });
            }
        } catch (error) {
            showMessage({ type: MESSAGE_TYPE.BAD, text: "No se pudo eliminar." });
        }
    };

    const handleSelectQuickReply = (text) => {
        setCurrentMessage(text);
        showMessage({ type: MESSAGE_TYPE.INFO, text: "Mensaje cargado. Da clic en Proyectar para enviarlo." });
    };

    // --------------------------------------------------------------------------------
    // 6. HANDLERS DE CONFIGURACIÓN VISUAL
    // --------------------------------------------------------------------------------
    const handleConfigChange = (key, value) => {
        setConfig(prev => ({ ...prev, [key]: value }));
    };

    const ReturnToDashboard = async () => {
        try {
            await HideInteractiveMessage(); // Limpiamos la pantalla por si acaso
            await BLResetCurrentDynamic();
        } finally {
            getOutOfTheDynamic();
        }
    };

    // --------------------------------------------------------------------------------
    // 7. RENDERIZADO
    // --------------------------------------------------------------------------------
    return (
        <SecondaryBase actionToExecute={ReturnToDashboard}>
            <div className="container-fluid py-4" style={{ maxWidth: '1000px' }}>
                
                {/* HEADER */}
                <div className="d-flex align-items-center justify-content-center mb-4">
                    <i className="bi bi-chat-square-quote-fill fs-2 me-3 text-white"></i>
                    <h2 className="mb-0 text-white fw-bold">Interacción en Vivo</h2>
                </div>

                <div className="row g-4">
                    
                    {/* ============================================================================== */}
                    {/* PANEL IZQUIERDO: CONTROL DE MENSAJES (Prioridad en Móvil) */}
                    {/* ============================================================================== */}
                    <div className="col-12 col-md-6">
                        <div className="card shadow border-danger bg-dark text-white h-100">
                            <div className="card-header bg-danger text-white fw-bold d-flex justify-content-between align-items-center">
                                <span><i className="bi bi-keyboard me-2"></i> Escribir Mensaje</span>
                                {isMessageVisible && <span className="badge bg-success">En Pantalla</span>}
                            </div>
                            <div className="card-body d-flex flex-column">
                                
                                <textarea 
                                    className="form-control mb-3 fs-5" 
                                    rows="3" 
                                    placeholder="Ej: ¿A quién le vas a dedicar el gol?"
                                    value={currentMessage}
                                    onChange={(e) => setCurrentMessage(e.target.value)}
                                    style={{ resize: 'none', backgroundColor: '#1a1a1a', color: 'white', border: '1px solid #444' }}
                                />

                                {/* BOTONES PRINCIPALES */}
                                <div className="d-flex flex-column gap-2 mb-4">
                                    <button 
                                        className={`btn btn-lg fw-bold ${isMessageVisible ? 'btn-outline-danger' : 'btn-danger'}`} 
                                        onClick={handleShowMessage}
                                    >
                                        <i className="bi bi-tv me-2"></i> 
                                        {isMessageVisible ? 'Actualizar Texto en Pantalla' : 'Proyectar Mensaje'}
                                    </button>
                                    
                                    <button 
                                        className="btn btn-outline-light fw-bold" 
                                        onClick={handleHideMessage}
                                        disabled={!isMessageVisible}
                                    >
                                        <i className="bi bi-eye-slash me-2"></i> Quitar de la Pantalla
                                    </button>
                                </div>

                                <hr className="border-secondary" />

                                {/* RESPUESTAS RÁPIDAS */}
                                <div className="d-flex justify-content-between align-items-center mb-2">
                                    <h6 className="mb-0 text-secondary fw-bold"><i className="bi bi-lightning-charge"></i> Respuestas Rápidas</h6>
                                    <button className="btn btn-sm btn-outline-info" onClick={handleSaveQuickReply} disabled={!currentMessage.trim()}>
                                        Guardar Actual
                                    </button>
                                </div>

                                <div className="d-flex flex-wrap gap-2 mt-2" style={{ maxHeight: '200px', overflowY: 'auto' }}>
                                    {isLoadingReplies ? (
                                        <span className="text-muted small">Cargando...</span>
                                    ) : quickReplies.length === 0 ? (
                                        <span className="text-muted small fst-italic">No has guardado respuestas rápidas.</span>
                                    ) : (
                                        quickReplies.map(reply => (
                                            <div key={reply.id} className="btn-group" role="group">
                                                <button type="button" className="btn btn-sm btn-secondary text-truncate" style={{ maxWidth: '150px' }} onClick={() => handleSelectQuickReply(reply.text)}>
                                                    {reply.text}
                                                </button>
                                                <button type="button" className="btn btn-sm btn-dark border-secondary text-danger" onClick={() => handleDeleteQuickReply(reply.id)} title="Borrar">
                                                    <i className="bi bi-x"></i>
                                                </button>
                                            </div>
                                        ))
                                    )}
                                </div>

                            </div>
                        </div>
                    </div>

                    {/* ============================================================================== */}
                    {/* PANEL DERECHO: CONFIGURACIÓN VISUAL */}
                    {/* ============================================================================== */}
                    <div className="col-12 col-md-6">
                        <div className="card shadow border-info bg-dark text-white h-100">
                            <div className="card-header bg-info text-white fw-bold">
                                <i className="bi bi-palette me-2"></i> Apariencia Visual
                            </div>
                            <div className="card-body">
                                
                                {/* FUENTE */}
                                <div className="mb-4">
                                    <label className="form-label small fw-bold text-muted">Tipo de Letra:</label>
                                    <select 
                                        className="form-select form-select-sm bg-dark text-white border-secondary"
                                        value={config.fontFamily}
                                        onChange={(e) => handleConfigChange('fontFamily', e.target.value)}
                                    >
                                        <option value="Montserrat">Montserrat (Predeterminada)</option>
                                        <option value="Bebas Neue">Bebas Neue (Impacto)</option>
                                        <option value="Roboto">Roboto (Limpia)</option>
                                        <option value="Caveat">Caveat (Decorativa)</option>
                                    </select>
                                </div>

                                {/* COLORES */}
                                <div className="row g-3 mb-4">
                                    <div className="col-4 text-center">
                                        <label className="d-block small fw-bold text-muted mb-1">Color Texto</label>
                                        <input type="color" className="form-control form-control-color w-100 bg-dark border-secondary p-1" value={config.textColor} onChange={(e) => handleConfigChange('textColor', e.target.value)} />
                                    </div>
                                    <div className="col-4 text-center">
                                        <label className="d-block small fw-bold text-muted mb-1">Color Borde</label>
                                        <input type="color" className="form-control form-control-color w-100 bg-dark border-secondary p-1" value={config.strokeColor} onChange={(e) => handleConfigChange('strokeColor', e.target.value)} />
                                    </div>
                                    <div className="col-4 text-center">
                                        <label className="d-block small fw-bold text-muted mb-1">Color Fondo</label>
                                        <input type="color" className="form-control form-control-color w-100 bg-dark border-secondary p-1" value={config.backgroundColor} onChange={(e) => handleConfigChange('backgroundColor', e.target.value)} disabled={!config.hasBackground} />
                                    </div>
                                </div>

                                {/* TOGGLE FONDO */}
                                <div className="form-check form-switch mb-4 pb-3 border-bottom border-secondary">
                                    <input className="form-check-input" type="checkbox" checked={config.hasBackground} onChange={(e) => handleConfigChange('hasBackground', e.target.checked)} />
                                    <label className="form-check-label small fw-bold">Mostrar plasta semitransparente de fondo</label>
                                </div>

                                {/* SLIDERS FÍSICA */}
                                <div className="mb-3">
                                    <label className="d-flex justify-content-between small fw-bold text-muted mb-1">
                                        <span>Tamaño (Escala)</span>
                                        <span className="text-info">{config.scale}x</span>
                                    </label>
                                    <input type="range" className="form-range" min="0.5" max="3" step="0.1" value={config.scale} onChange={(e) => handleConfigChange('scale', parseFloat(e.target.value))} />
                                </div>

                                <div className="mb-3">
                                    <label className="d-flex justify-content-between small fw-bold text-muted mb-1">
                                        <span>Posición Vertical (Y)</span>
                                        <span className="text-info">{config.positionY}px</span>
                                    </label>
                                    <input type="range" className="form-range" min="0" max="800" step="10" value={config.positionY} onChange={(e) => handleConfigChange('positionY', parseInt(e.target.value))} />
                                    <small className="text-muted" style={{fontSize: '0.65rem'}}>Mueve el texto desde abajo hacia arriba</small>
                                </div>

                                <div>
                                    <label className="d-flex justify-content-between small fw-bold text-muted mb-1">
                                        <span>Inclinación (Rotación)</span>
                                        <span className="text-info">{config.rotation}°</span>
                                    </label>
                                    <input type="range" className="form-range" min="-45" max="45" step="1" value={config.rotation} onChange={(e) => handleConfigChange('rotation', parseInt(e.target.value))} />
                                </div>

                            </div>
                        </div>
                    </div>

                </div>
            </div>
        </SecondaryBase>
    );
};

export { InteractiveChat };